import "@babel/polyfill";
import "mutationobserver-shim";
import { createApp, h } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
// NOTE: plugins 以下から import するのがベストプラクティスなのか悩ましいが、一旦既存の構成に合わせておく
import { BootstrapVue, BootstrapVueIcons } from "@/plugins/bootstrap-vue";
import TextFieldGroup from "@/components/molecules/form/TextFieldGroup.vue";
import Loading from "@/components/organisms/common/Loading.vue";
import { LoadingManager } from "@/plugins/loading";
import {
  ValidationField,
  ValidationForm,
  ValidationObserver,
  ValidationProvider
} from "@/plugins/vee-validate";
import TextareaAutosize from "vue-textarea-autosize";
// Vue3 用の vue-select 型定義は存在しない
// @ts-ignore
import VueSelect from "vue-select";

import "vue-select/dist/vue-select.css";
import "@/assets/scss/custom.scss";
import { masterStore } from "./store/master";

import { initSentry, initMarketoMunchkin } from "./externalModuleInitializer";
import CustomValidationObserver from "./plugins/CustomValidationObserver.vue";
import { authStore } from "@/store/auth";

const MASTER_FETCH_RETRIED_KEY = "masterFetchRetriedCount";
const MAX_RETRY_COUNT = 3;

async function fetchMasterWithErrorHandling(): Promise<boolean> {
  try {
    await masterStore.fetchMaster();
    return true;
  } catch (error) {
    // @ts-ignore
    const status: number = error.response?.status;
    if (status !== 401) {
      // ここではまだVueが使えないのでalertを使う
      alert(
        "マスターデータの取得に失敗しました。ネットワークの状態を確認してください。"
      );
      return false;
    }

    // 401エラーの場合は認証エラーとして特別に処理する
    // masterのエンドポイントは認証情報を必要としない
    // しかし、認証エラーとなる場合がある。有効期限切れなどが考えられるが、
    // この場合は認証情報がおかしいので一旦認証情報を削除してリロードする
    authStore.logout();
    localStorage.clear();

    // 無限リロード対策
    const retriedCount = parseInt(
      sessionStorage.getItem(MASTER_FETCH_RETRIED_KEY) || "0",
      10
    );

    // リトライ回数を増加
    sessionStorage.setItem(MASTER_FETCH_RETRIED_KEY, String(retriedCount + 1));

    if (retriedCount >= MAX_RETRY_COUNT) {
      // 上限に達した場合はエラーメッセージを表示して処理を停止
      sessionStorage.removeItem(MASTER_FETCH_RETRIED_KEY);
      alert("認証エラーが継続しています。サポートにお問い合わせください。");
    } else {
      location.reload();
    }
    return false;
  }
}

// グローバルに外部モジュールをコールする場合、ここに記載する。
initMarketoMunchkin();

async function init() {
  // masterを取得してから起動
  const success = await fetchMasterWithErrorHandling();
  // masterの取得に失敗した場合は、アプリケーションを起動しない
  // 例外処理はfetchMasterWithErrorHandling内で行われている
  if (!success) return;

  const app = createApp(App)
    .use(router)
    .use(store)
    .use(BootstrapVue)
    .use(BootstrapVueIcons)
    .use(TextareaAutosize)
    .component("ValidationField", ValidationField)
    .component("ValidationForm", ValidationForm)
    .component("ValidationObserver", ValidationObserver)
    .component("ValidationProvider", ValidationProvider)
    .component("TextFieldGroup", TextFieldGroup)
    .component("VSelect", VueSelect)
    .component("CustomValidationObserver", CustomValidationObserver)
    .use({
      install: app => {
        const node = document.createElement("div");
        document.body.appendChild(node);

        const loading = createApp({
          components: {
            Loading
          },
          render: () => h(Loading, { ref: "loading" })
        }).mount(node);
        app.config.globalProperties.$loading = new LoadingManager(loading);
      }
    });

  initSentry(app);

  const instance = app.mount("#app");

  // router.app は削除されたため自前で再設定する
  // https://router.vuejs.org/guide/migration/#Removal-of-router-app
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (router as any).app = instance;
}

init();
